import React, { useEffect, useState, useCallback } from 'react';
import { articlesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Layers, Search, Edit, Save, Volume2, Upload, X, Download, ExternalLink, Trash2, FileUp, Loader2, Star, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function PublishedPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editArticle, setEditArticle] = useState(null);
  const [audioUpload, setAudioUpload] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [pdfUpload, setPdfUpload] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [togglingFeatured, setTogglingFeatured] = useState(null);
  const { user } = useAuth();
  const isAdmin = () => user?.roles?.includes('Admin');
  const isEditor = () => user?.roles?.includes('Editor');
  const [downloading, setDownloading] = useState(null);

  const handleToggleFeatured = async (a) => {
    setTogglingFeatured(a.id);
    try {
      const res = await articlesAPI.setFeatured(a.id, !a.featured);
      toast.success(res.data.message);
      setArticles(list => list.map(x => x.id === a.id ? { ...x, featured: !a.featured } : x));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar el destacado');
    }
    setTogglingFeatured(null);
  };

  const handleDownloadArticle = async (articleId) => {
    setDownloading(articleId);
    try {
      await articlesAPI.downloadBlob(articleId);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar el articulo. Intenta de nuevo.');
    } finally {
      setDownloading(null);
    }
  };

  const loadArticles = useCallback(async () => {
    try {
      const res = await articlesAPI.list();
      setArticles(res.data.items || res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  const filtered = articles.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.titulo?.toLowerCase().includes(q) || a.autores?.toLowerCase().includes(q);
  });

  const handleUpdateArticle = async () => {
    if (!editArticle) return;
    try {
      await articlesAPI.update(editArticle.id, {
        doi: editArticle.doi,
        titulo: editArticle.titulo,
        volumen: editArticle.volumen,
        numero: editArticle.numero
      });
      setEditArticle(null);
      toast.success('Articulo actualizado');
      loadArticles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleUploadAudio = async (articleId) => {
    if (!audioUpload?.file) return;
    setAudioUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', audioUpload.file);
      await articlesAPI.uploadAudio(articleId, fd);
      setAudioUpload(null);
      toast.success('Audio subido exitosamente');
      loadArticles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir audio'); }
    setAudioUploading(false);
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Eliminar este articulo publicado? Esta accion no se puede deshacer.')) return;
    try {
      await articlesAPI.delete(articleId);
      toast.success('Articulo eliminado');
      loadArticles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleDeleteAudio = async (articleId) => {
    if (!window.confirm('Eliminar el audio de este articulo?')) return;
    try { await articlesAPI.deleteAudio(articleId); toast.success('Audio eliminado'); loadArticles(); } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar audio'); }
  };

  const handleUploadPdf = async (articleId) => {
    if (!pdfUpload?.file) return;
    setPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pdfUpload.file);
      await articlesAPI.uploadPdf(articleId, fd);
      setPdfUpload(null);
      toast.success('PDF actualizado exitosamente');
      loadArticles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir PDF'); }
    setPdfUploading(false);
  };

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="published-page">
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-dark">Articulos Publicados</h1>
            <p className="text-sm font-sans text-gray-400 mt-1">{articles.length} articulo{articles.length !== 1 ? 's' : ''} publicado{articles.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="relative max-w-sm mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por titulo o autore..." className="pl-9 font-sans text-sm" data-testid="search-articles" />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-sm border border-gray-100">
            <Layers className="w-10 h-10 text-gray-200 mx-auto mb-3" strokeWidth={1} />
            <p className="font-sans text-gray-400">No hay articulos publicados aun.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-sm divide-y divide-gray-50">
            {filtered.map((a, i) => (
              <div key={a.id} className="p-5 hover:bg-gray-50/30 transition-colors" data-testid={`published-article-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-green-50 border border-green-200 hidden sm:flex">
                    <Layers className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-sans bg-green-50 text-green-700 border-green-200">Publicado</Badge>
                      {a.featured && (
                        <Badge className="text-[10px] font-sans bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50" data-testid={`featured-badge-${i}`}>
                          <Star className="w-3 h-3 mr-1" fill="currentColor" /> Destacado
                        </Badge>
                      )}
                      {a.categoria && <span className="text-[10px] font-sans text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{a.categoria}</span>}
                      <span className="text-[10px] font-sans text-gray-400">Vol. {a.volumen} N. {a.numero}</span>
                      {a.rating_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-sans text-amber-600" data-testid={`rating-${i}`}><Star className="w-3 h-3" fill="currentColor" /> {a.rating_avg.toFixed(1)} ({a.rating_count})</span>
                      )}
                      {a.comments_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-sans text-gray-400" data-testid={`comments-${i}`}><MessageSquare className="w-3 h-3" /> {a.comments_count}</span>
                      )}
                    </div>
                    <h3 className="font-serif font-semibold text-slate-dark leading-snug">{a.titulo}</h3>
                    <p className="text-xs font-sans text-gray-400 mt-1">
                      <span className="font-medium">{a.autores}</span>
                      {a.doi && <> &middot; DOI: {a.doi}</>}
                      {a.published_at && <> &middot; {new Date(a.published_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                    </p>
                    {a.abstract_es && (
                      <p className="text-sm font-serif text-gray-500 mt-2 line-clamp-2">{a.abstract_es}</p>
                    )}
                    {a.audio_name && (
                      <div className="flex items-center gap-2 mt-2">
                        <Volume2 className="w-3 h-3 text-trans-blue" />
                        <span className="text-xs font-sans text-trans-blue">{a.audio_name}</span>
                        {(isAdmin() || isEditor()) && (
                          <button onClick={() => handleDeleteAudio(a.id)} className="text-xs text-red-400 hover:text-red-600" data-testid={`delete-audio-${i}`}>
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3 sm:mt-0 sm:flex-col sm:shrink-0">
                    {a.file_available && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="font-sans text-xs h-8"
                        disabled={downloading === a.id}
                        onClick={() => handleDownloadArticle(a.id)}
                        data-testid={`download-article-${i}`}
                      >
                        {downloading === a.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} PDF
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="font-sans text-xs h-8" asChild data-testid={`view-article-${i}`}>
                      <a href={`/articulo/${a.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3 mr-1" /> Ver
                      </a>
                    </Button>
                    {(isAdmin() || isEditor()) && (
                      <>
                        {isAdmin() && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={`font-sans text-xs h-8 ${a.featured ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                            disabled={togglingFeatured === a.id}
                            onClick={() => handleToggleFeatured(a)}
                            data-testid={`toggle-featured-${i}`}
                          >
                            {togglingFeatured === a.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Star className="w-3 h-3 mr-1" fill={a.featured ? 'currentColor' : 'none'} />}
                            {a.featured ? 'Quitar destacado' : 'Destacar'}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="font-sans text-xs h-8 text-green-600 border-green-200" onClick={() => setPdfUpload({ articleId: a.id, file: null })} data-testid={`upload-pdf-btn-${i}`}>
                          <FileUp className="w-3 h-3 mr-1" /> {a.file_available ? 'Cambiar PDF' : 'Subir PDF'}
                        </Button>
                        {!a.audio_name && (
                          <Button variant="outline" size="sm" className="font-sans text-xs h-8 text-trans-blue border-trans-blue/30" onClick={() => setAudioUpload({ articleId: a.id, file: null })} data-testid={`upload-audio-btn-${i}`}>
                            <Volume2 className="w-3 h-3 mr-1" /> Audio
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="font-sans text-xs h-8" onClick={() => setEditArticle({ ...a })} data-testid={`edit-article-${i}`}>
                          <Edit className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        {isAdmin() && (
                          <Button variant="ghost" size="sm" className="font-sans text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteArticle(a.id)} data-testid={`delete-article-${i}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Article Dialog */}
      <Dialog open={!!editArticle} onOpenChange={() => setEditArticle(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Editar Articulo</DialogTitle></DialogHeader>
          {editArticle && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-sans font-medium">Titulo</label>
                <Input value={editArticle.titulo} onChange={e => setEditArticle(a => ({ ...a, titulo: e.target.value }))} className="mt-1 font-sans" />
              </div>
              <div>
                <label className="text-sm font-sans font-medium">DOI</label>
                <Input value={editArticle.doi || ''} onChange={e => setEditArticle(a => ({ ...a, doi: e.target.value }))} className="mt-1 font-sans" placeholder="10.xxxxx/xxxxx" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-sans font-medium">Volumen</label>
                  <Input type="number" value={editArticle.volumen} onChange={e => setEditArticle(a => ({ ...a, volumen: parseInt(e.target.value) || 1 }))} className="mt-1 font-sans" />
                </div>
                <div>
                  <label className="text-sm font-sans font-medium">Numero</label>
                  <Input type="number" value={editArticle.numero} onChange={e => setEditArticle(a => ({ ...a, numero: parseInt(e.target.value) || 1 }))} className="mt-1 font-sans" />
                </div>
              </div>
              <Button onClick={handleUpdateArticle} className="w-full bg-slate-dark font-sans" data-testid="save-article-btn"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audio Upload Dialog */}
      <Dialog open={!!audioUpload} onOpenChange={() => setAudioUpload(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Subir Audio del Articulo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-sans text-gray-500">Selecciona un archivo de audio (MP3, WAV, OGG) para asociar a este articulo.</p>
            <div className="border-2 border-dashed border-gray-200 rounded-md p-6 text-center hover:border-trans-blue/50 transition-colors">
              <input type="file" accept=".mp3,.wav,.ogg,.m4a,.aac" onChange={e => setAudioUpload(prev => ({ ...prev, file: e.target.files[0] }))} className="hidden" id="audio-upload-pub" data-testid="audio-file-input" />
              <label htmlFor="audio-upload-pub" className="cursor-pointer">
                {audioUpload?.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <Volume2 className="w-5 h-5 text-trans-blue" strokeWidth={1.5} />
                    <span className="text-sm font-sans text-slate-dark">{audioUpload.file.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" strokeWidth={1} />
                    <p className="text-sm font-sans text-gray-400">Haz clic para seleccionar archivo</p>
                    <p className="text-xs font-sans text-gray-300 mt-1">MP3, WAV, OGG</p>
                  </div>
                )}
              </label>
            </div>
            <Button onClick={() => handleUploadAudio(audioUpload?.articleId)} className="w-full bg-slate-dark font-sans" disabled={!audioUpload?.file || audioUploading} data-testid="confirm-audio-upload-btn">
              {audioUploading ? 'Subiendo...' : <><Upload className="w-4 h-4 mr-1" /> Subir Audio</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* PDF Upload Dialog */}
      <Dialog open={!!pdfUpload} onOpenChange={() => setPdfUpload(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Subir PDF del Articulo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-sans text-gray-500">Selecciona el archivo PDF del articulo. Si ya existe un PDF, sera reemplazado.</p>
            <div className="border-2 border-dashed border-gray-200 rounded-md p-6 text-center hover:border-green-400/50 transition-colors">
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setPdfUpload(prev => ({ ...prev, file: e.target.files[0] }))} className="hidden" id="pdf-upload-pub" data-testid="pdf-file-input" />
              <label htmlFor="pdf-upload-pub" className="cursor-pointer">
                {pdfUpload?.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileUp className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                    <span className="text-sm font-sans text-slate-dark">{pdfUpload.file.name}</span>
                  </div>
                ) : (
                  <div>
                    <FileUp className="w-8 h-8 text-gray-300 mx-auto mb-2" strokeWidth={1} />
                    <p className="text-sm font-sans text-gray-400">Haz clic para seleccionar archivo</p>
                    <p className="text-xs font-sans text-gray-300 mt-1">PDF, DOC, DOCX</p>
                  </div>
                )}
              </label>
            </div>
            <Button onClick={() => handleUploadPdf(pdfUpload?.articleId)} className="w-full bg-slate-dark font-sans" disabled={!pdfUpload?.file || pdfUploading} data-testid="confirm-pdf-upload-btn">
              {pdfUploading ? 'Subiendo...' : <><FileUp className="w-4 h-4 mr-1" /> Subir PDF</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
